import { Box, Stack, Typography, useTheme } from '@mui/material'
import { ArcElement, Chart, Legend, Tooltip } from 'chart.js'
import React from 'react'
import { Pie } from 'react-chartjs-2'
import { useLocation } from 'react-router'

import { GINGIVA_PIE_COLORS, PIE_COLORS } from '@/utils/constants'

// Register necessary Chart.js components
Chart.register(ArcElement, Tooltip, Legend)

type PieChartEntity = {
  labels: string[]
  values: number[]
  height?: string
  width?: string
}

const PieChart = ({
  labels,
  values,
  height = '100px',
  width = '100px',
}: PieChartEntity) => {
  const location = useLocation()

  const isGingivaStainingPage = [
    'staining-studio/gingiva-staining',
    'gingiva/staining',
    'gingiva/ceramic',
    'pfm-crown/dentin',
    'full-layering/dentin',
  ].some((path) => location.pathname.includes(path))

  const theme = useTheme()

  const chartData = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: (isGingivaStainingPage
          ? GINGIVA_PIE_COLORS
          : PIE_COLORS
        ).slice(0, labels?.length),
        borderWidth: 0,
        hoverBackgroundColor: ['#FFFFFF'],
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      {/* Fixed-size Container */}
      <Box
        sx={{
          height: height,
          width: width,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {labels?.length && values?.length ? (
          <Pie data={chartData} options={chartOptions} />
        ) : (
          <Typography
            variant="subtitle1"
            color={theme.palette.action.disabled}
            fontSize={'14px'}
            fontWeight={400}
            lineHeight={'20px'}
          >
            N/A
          </Typography>
        )}
      </Box>

      {/* Custom Legend */}
      <Stack
        spacing={1}
        mt={isGingivaStainingPage ? '4px' : 3}
        alignItems="flex-start"
      >
        {labels?.map((label, i) => (
          <Stack
            key={`${label}-${values[i]}`}
            direction="row"
            alignItems="center"
            spacing={1}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: !isGingivaStainingPage
                  ? PIE_COLORS[i]
                  : GINGIVA_PIE_COLORS[i],
              }}
            />
            <Typography
              noWrap
              display="flex"
              alignItems="center"
              sx={{ maxWidth: '200px' }}
              title={`${values[i]} - ${label}`}
              fontSize={'14px'}
              fontWeight={400}
              lineHeight={'20px'}
              bgcolor={isGingivaStainingPage ? 'rgba(33, 33, 33, 0.80)' : ''}
              padding="2px 6px"
              borderRadius="4px"
            >
              {values[i]} - {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

export default PieChart
